/**
 * AdminMaintenance — danger-zone admin tools.
 *
 * Currently exposes:
 *   • Wipe test data — clears orders / transactions / messages / login
 *     events / etc. for a list of emails. Optionally also deletes the
 *     user accounts themselves. Backend protects the calling admin and
 *     other admin accounts from accidental wipe; this UI surfaces the
 *     "skipped" feedback so the admin sees why a target was untouched.
 *
 * Lives in Settings → System → Maintenance — far enough from day-to-day
 * editors that you can't trip over it by accident.
 */
import React, { useState } from 'react';import { AlertTriangle, Loader2, Trash2, Calculator, AlertCircle, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import DetectedTestOrdersPanel from './admin/DetectedTestOrdersPanel';
import ManualOrderCleanupPanel from './admin/ManualOrderCleanupPanel';
import PasswordAuditPanel from './admin/PasswordAuditPanel';
import LegacySyncPanel from './admin/LegacySyncPanel';
import ReservedWalletAuditPanel from './admin/ReservedWalletAuditPanel';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminMaintenance({ token }) {
  const [emails, setEmails] = useState('');
  const [deleteUsers, setDeleteUsers] = useState(false);
  const [wipeAdmins, setWipeAdmins] = useState(false);
  const [busy, setBusy] = useState(false);
  const [lastResult, setLastResult] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const parseEmails = () =>
    emails.split(/[\s,;\n]+/).map((s) => s.trim()).filter(Boolean);

  const run = async () => {
    const list = parseEmails();
    if (list.length === 0) {
      toast.error('Enter at least one email');
      return;
    }
    setBusy(true);
    setLastResult(null);
    try {
      const r = await fetch(`${API_URL}/api/admin/wipe-test-data`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          emails: list,
          delete_users: deleteUsers,
          wipe_admins:  wipeAdmins,
        }),
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(data?.detail || `HTTP ${r.status}`);
      setLastResult(data);
      const totalDeleted = Object.values(data.deleted || {}).reduce((a, b) => a + (b || 0), 0);
      toast.success(
        `Wiped ${totalDeleted.toLocaleString()} records`,
        { description: `${data.users_deleted ? `${data.users_deleted} user(s) removed · ` : ''}${data.skipped?.length || 0} skipped` },
      );
      setConfirmOpen(false);
    } catch (e) {
      toast.error('Wipe failed', { description: e.message });
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-5" data-testid="admin-maintenance-page">
      <header>
        <h2 className="text-xl font-bold text-slate-800 flex items-center gap-2">
          <AlertTriangle className="w-5 h-5 text-amber-600" /> Maintenance
          <span className="text-[11px] font-normal text-slate-500">Danger-zone admin operations.</span>
        </h2>
      </header>

      {/* Detected test orders — surfaced ABOVE the email-based wipe tool
          because it's the more frequent maintenance need (cleaning up
          recent regression / smoke-test batches one at a time). */}
      <DetectedTestOrdersPanel token={token} />

      {/* Manual order cleanup — paste-and-pick flow for admin-owned test
          orders that won't trigger the auto-detector (real-looking
          email, normal vehicle, etc.). */}
      <ManualOrderCleanupPanel token={token} />

      {/* Phase E — Admin Security panel. Read-mostly dashboard of every
          signal in the auth/security subsystem: session policy, admin
          MFA roster, Google OAuth, SMTP, rate-limit buckets, audit log. */}

      {/* Password audit — read-only inspector for password_hash_history.
          Lets admin verify whether anything (script, cron, recovery,
          legitimate forgot-password reset) has touched a given user's
          password_hash. No hash values are ever exposed; no write
          actions are offered from this panel. */}
      <PasswordAuditPanel token={token} />

      {/* Legacy CSV API delta sync — restores the Daily catch-up workflow
          for pulling accounts/transactions/orders/tickets from the old
          Yii1 site (fs.auto-remap.com). Backend routes still live in
          `routes/legacy_delta_sync.py`. */}
      <LegacySyncPanel token={token} />

      {/* The Catalog Mirror (Supabase Phase D) panel is removed in the
          Excel-only migration — Mongo `price_catalog` is the only
          catalog store now. */}

      <section className="bg-white border border-rose-200 rounded-xl shadow-sm p-4 sm:p-5" data-testid="admin-wipe-card">
        <div className="flex items-center gap-2 mb-3">
          <div className="w-9 h-9 rounded-lg bg-rose-50 flex items-center justify-center">
            <Trash2 className="w-5 h-5 text-rose-600" />
          </div>
          <div>
            <h3 className="font-semibold text-slate-800">Wipe test data</h3>
            <p className="text-[12px] text-slate-500">
              Clears orders, transactions, ticket messages, login logs, EVC purchases & payment events for the given emails.
              The Original Files catalog and admin config are <b>not</b> touched.
            </p>
          </div>
        </div>

        <label className="block">
          <span className="text-[11px] font-bold uppercase tracking-wider text-slate-500">
            Emails (comma- or newline-separated)
          </span>
          <textarea
            rows={3}
            value={emails}
            onChange={(e) => setEmails(e.target.value)}
            placeholder="demo@auto-remap.com, test1@x.com"
            className="mt-1 w-full border border-slate-300 rounded-lg px-3 py-2 text-sm font-mono"
            data-testid="admin-wipe-emails"
          />
        </label>

        <div className="mt-3 flex flex-wrap items-center gap-4">
          <label className="inline-flex items-center gap-2 text-sm">
            <input
              type="checkbox" checked={deleteUsers}
              onChange={(e) => setDeleteUsers(e.target.checked)}
              data-testid="admin-wipe-delete-users"
            />
            <span>Also <b>delete the user accounts</b> (otherwise wallet/EVC are reset to 0 and the account stays).</span>
          </label>
          <label className="inline-flex items-center gap-2 text-sm">
            <input
              type="checkbox" checked={wipeAdmins}
              onChange={(e) => setWipeAdmins(e.target.checked)}
              data-testid="admin-wipe-include-admins"
            />
            <span>Allow wiping admin accounts <span className="text-slate-500">(your own is always protected)</span></span>
          </label>
        </div>

        <div className="mt-4 flex flex-wrap gap-2">
          <button
            onClick={() => setConfirmOpen(true)} disabled={busy || !emails.trim()}
            className="px-4 py-2 rounded-lg font-bold text-sm flex items-center gap-2 bg-rose-600 hover:bg-rose-700 text-white shadow-sm disabled:opacity-50"
            data-testid="admin-wipe-go"
          >
            <Trash2 className="w-4 h-4" /> Wipe data
          </button>
          {emails.trim() && (
            <span className="text-xs text-slate-500 self-center">
              {parseEmails().length} email(s) parsed
            </span>
          )}
        </div>
      </section>

      {/* Result panel */}
      {lastResult && (
        <section className="bg-white border border-slate-200 rounded-xl shadow-sm p-4 sm:p-5" data-testid="admin-wipe-result">
          <h3 className="font-semibold text-slate-800 mb-2">Last wipe result</h3>
          {lastResult.deleted && Object.keys(lastResult.deleted).length > 0 ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2 mb-3">
              {Object.entries(lastResult.deleted).map(([k, v]) => (
                <div key={k} className="rounded-lg bg-slate-50 border border-slate-200 px-3 py-2">
                  <p className="text-[10px] uppercase font-bold tracking-wider text-slate-500">{k.replace(/_/g, ' ')}</p>
                  <p className="text-lg font-extrabold tabular-nums">{Number(v || 0).toLocaleString()}</p>
                </div>
              ))}
              {lastResult.users_deleted ? (
                <div className="rounded-lg bg-rose-50 border border-rose-200 px-3 py-2">
                  <p className="text-[10px] uppercase font-bold tracking-wider text-rose-600">users deleted</p>
                  <p className="text-lg font-extrabold tabular-nums text-rose-700">{lastResult.users_deleted}</p>
                </div>
              ) : null}
            </div>
          ) : (
            <p className="text-sm text-slate-500 mb-3">Nothing was deleted.</p>
          )}
          {(lastResult.skipped?.length || 0) > 0 && (
            <div className="rounded-lg p-3 text-xs bg-amber-50 border border-amber-200">
              <p className="font-semibold text-amber-700 mb-1">Skipped:</p>
              <ul className="list-disc list-inside text-slate-700">
                {lastResult.skipped.map((s, i) => (
                  <li key={i}><b className="font-mono">{s.email}</b> — {s.reason}</li>
                ))}
              </ul>
            </div>
          )}
        </section>
      )}

      {/* ── Recompute wallet balances ─────────────────────── */}
      <RecomputeBalanceCard token={token} />

      {/* ── Weekly reconciler ─────────────────────── */}
      <WalletAuditCard token={token} />

      {/* ── Per-customer reserved fix (Phase H45) ── */}
      <ReservedWalletAuditPanel token={token} />

      {/* Confirmation modal */}
      {confirmOpen && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" data-testid="admin-wipe-confirm">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6">
            <div className="flex items-start gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-rose-100 flex items-center justify-center flex-shrink-0">
                <AlertTriangle className="w-5 h-5 text-rose-600" />
              </div>
              <div>
                <h3 className="font-bold text-lg text-slate-800">Wipe test data?</h3>
                <p className="text-sm text-slate-600 mt-1">
                  This is <b>irreversible</b>. Orders, transactions and history for these emails will be permanently deleted:
                </p>
              </div>
            </div>
            <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 max-h-32 overflow-auto mb-4">
              <ul className="text-xs font-mono text-slate-800 space-y-0.5">
                {parseEmails().map((e, i) => <li key={i}>• {e}</li>)}
              </ul>
            </div>
            {deleteUsers && (
              <p className="text-xs text-rose-700 font-bold mb-2 flex items-center gap-1">
                <AlertTriangle className="w-3.5 h-3.5" /> User accounts will also be DELETED.
              </p>
            )}
            <div className="flex gap-2 justify-end">
              <button
                onClick={() => setConfirmOpen(false)} disabled={busy}
                className="px-4 py-2 rounded-lg text-sm font-semibold bg-slate-100 hover:bg-slate-200 text-slate-700 disabled:opacity-50"
                data-testid="admin-wipe-cancel"
              >Cancel</button>
              <button
                onClick={run} disabled={busy}
                className="px-4 py-2 rounded-lg text-sm font-bold bg-rose-600 hover:bg-rose-700 text-white flex items-center gap-2 disabled:opacity-50"
                data-testid="admin-wipe-confirm-btn"
              >
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                {busy ? 'Wiping…' : 'Yes, wipe'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * RecomputeBalanceCard — audit-recovery widget.
 *
 * Two-step flow:
 *   1. Admin types a list of emails (or leaves blank for all 5k users)
 *      and clicks "Preview" → backend returns dry-run rows showing
 *      stored vs. computed balance plus diff.
 *   2. If there are drifted rows, "Apply fixes" button writes the
 *      computed balances back to the user docs.
 *
 * Why a second click between preview and apply: drift is rare and
 * worth eyeballing. The preview also doubles as a sanity check that
 * the admin's email list matches who they intended.
 */
function RecomputeBalanceCard({ token }) {
  const [emails, setEmails] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const parseEmails = () =>
    emails.split(/[\s,;\n]+/).map((s) => s.trim()).filter(Boolean);

  const run = async (dryRun) => {
    setBusy(true);
    try {
      const list = parseEmails();
      const body = { dry_run: dryRun };
      if (list.length > 0) body.emails = list;
      const r = await fetch(`${API_URL}/api/admin/recompute-balances`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(body),
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(data?.detail || `HTTP ${r.status}`);
      setResult({ ...data, dry_run: dryRun });
      if (dryRun) {
        toast.success(
          `Checked ${data.summary.checked} user(s)`,
          { description: data.summary.drifted > 0
              ? `${data.summary.drifted} drifted — review then "Apply fixes" to write.`
              : 'No drift detected — ledger and wallet are in sync.' },
        );
      } else {
        toast.success(
          `Fixed ${data.summary.fixed} user(s)`,
          { description: data.summary.drifted === data.summary.fixed
              ? 'All discrepancies recompiled.'
              : `${data.summary.drifted - data.summary.fixed} could not be auto-fixed.` },
        );
      }
    } catch (e) {
      toast.error('Recompute failed', { description: e.message });
    } finally { setBusy(false); }
  };

  const drifted = (result?.rows || []).filter((r) => Math.abs(r.diff || 0) > 0.005);
  const showApply = result?.dry_run && drifted.length > 0;

  return (
    <section className="bg-white border border-cyan-200 rounded-xl shadow-sm p-4 sm:p-5" data-testid="admin-recompute-card">
      <div className="flex items-center gap-2 mb-3">
        <div className="w-9 h-9 rounded-lg bg-cyan-50 flex items-center justify-center">
          <Calculator className="w-5 h-5 text-cyan-600" />
        </div>
        <div>
          <h3 className="font-semibold text-slate-800">Recompute wallet balances</h3>
          <p className="text-[12px] text-slate-500">
            Re-derives each user's wallet from their transaction ledger and shows where the cache disagrees.
            The ledger is the source of truth — anything else is a cache bug.
          </p>
        </div>
      </div>

      <label className="block">
        <span className="text-[11px] font-bold uppercase tracking-wider text-slate-500">
          Emails (leave empty to scan all up to 5,000 users)
        </span>
        <textarea
          rows={2}
          value={emails}
          onChange={(e) => setEmails(e.target.value)}
          placeholder="empty = all users · or: demo@auto-remap.com, alice@x.com"
          className="mt-1 w-full border border-slate-300 rounded-lg px-3 py-2 text-sm font-mono"
          data-testid="admin-recompute-emails"
        />
      </label>

      <div className="mt-3 flex flex-wrap gap-2">
        <button
          onClick={() => run(true)} disabled={busy}
          className="px-4 py-2 rounded-lg font-bold text-sm flex items-center gap-2 bg-cyan-600 hover:bg-cyan-700 text-white shadow-sm disabled:opacity-50"
          data-testid="admin-recompute-preview"
        >
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Calculator className="w-4 h-4" />}
          Preview drift
        </button>
        {showApply && (
          <button
            onClick={() => run(false)} disabled={busy}
            className="px-4 py-2 rounded-lg font-bold text-sm flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white shadow-sm disabled:opacity-50"
            data-testid="admin-recompute-apply"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
            Apply fixes ({drifted.length})
          </button>
        )}
      </div>

      {result && (
        <div className="mt-4 space-y-3" data-testid="admin-recompute-result">
          {/* Summary line */}
          <div className={`rounded-lg p-3 text-xs flex items-center gap-2 ${result.summary.drifted > 0 ? 'bg-amber-50 border border-amber-200 text-amber-800' : 'bg-emerald-50 border border-emerald-200 text-emerald-800'}`}>
            {result.summary.drifted > 0
              ? <AlertCircle className="w-4 h-4" />
              : <CheckCircle2 className="w-4 h-4" />}
            <span>
              <b>{result.summary.checked.toLocaleString()}</b> checked
              {' · '}
              <b>{result.summary.drifted}</b> drifted
              {!result.dry_run ? <> · <b>{result.summary.fixed}</b> fixed</> : null}
              {result.dry_run && result.summary.drifted > 0 ? ' (preview only — nothing written yet)' : null}
            </span>
          </div>

          {drifted.length > 0 && (
            <div className="overflow-x-auto rounded-lg border border-slate-200">
              <table className="w-full text-xs">
                <thead className="bg-slate-50">
                  <tr className="text-left text-[10px] uppercase tracking-wider text-slate-500">
                    <th className="px-3 py-2 whitespace-nowrap">Email</th>
                    <th className="px-3 py-2 text-right whitespace-nowrap">Stored</th>
                    <th className="px-3 py-2 text-right whitespace-nowrap">Computed</th>
                    <th className="px-3 py-2 text-right whitespace-nowrap">Diff</th>
                  </tr>
                </thead>
                <tbody>
                  {drifted.slice(0, 100).map((r) => (
                    <tr key={r.user_id} className="border-t border-slate-100">
                      <td className="px-3 py-2 font-mono">{r.email}</td>
                      <td className="px-3 py-2 text-right tabular-nums">€{r.current_balance.toFixed(2)}</td>
                      <td className="px-3 py-2 text-right tabular-nums">€{r.computed_balance.toFixed(2)}</td>
                      <td className={`px-3 py-2 text-right font-bold tabular-nums ${r.diff < 0 ? 'text-rose-700' : 'text-emerald-700'}`}>
                        {r.diff > 0 ? '+' : ''}€{r.diff.toFixed(2)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {drifted.length > 100 && (
                <p className="px-3 py-2 text-[11px] text-slate-500 bg-slate-50 border-t border-slate-200">
                  Showing 100 of {drifted.length} drifted rows.
                </p>
              )}
            </div>
          )}
        </div>
      )}
    </section>
  );
}


/**
 * WalletAuditCard — surfaces the weekly reconciler.
 * Shows the last run summary and lets the admin trigger an on-demand pass.
 */
function WalletAuditCard({ token }) {
  const [last, setLast] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const [running, setRunning] = React.useState(false);

  const fetchLast = React.useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API_URL}/api/admin/wallet-audit/last`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const j = await r.json();
      setLast(j);
    } catch (e) {
      toast.error('Could not fetch audit status');
    } finally { setLoading(false); }
  }, [token]);

  React.useEffect(() => { fetchLast(); }, [fetchLast]);

  const runNow = async () => {
    setRunning(true);
    try {
      const r = await fetch(`${API_URL}/api/admin/wallet-audit/run`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}` },
      });
      const j = await r.json();
      if (!r.ok) throw new Error(j.detail || 'Audit failed');
      setLast(j);
      toast.success(j.findings?.length ? `Audit: ${j.findings.length} discrepancies` : 'Audit clean ✓');
    } catch (e) {
      toast.error(e.message);
    } finally { setRunning(false); }
  };

  const findings = last?.findings || [];
  const ok = !!last?.ok;

  return (
    <section className="bg-white rounded-xl border border-emerald-200 shadow-sm p-5 sm:p-6 mt-5" data-testid="wallet-audit-card">
      <header className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Calculator className="w-5 h-5 text-emerald-600" />
          <h2 className="text-base font-bold text-slate-800">Wallet audit</h2>
          {ok && findings.length === 0 && (
            <span className="text-[11px] font-bold px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">CLEAN</span>
          )}
          {ok && findings.length > 0 && (
            <span className="text-[11px] font-bold px-2 py-0.5 rounded-full bg-rose-50 text-rose-700 border border-rose-200">{findings.length} drift</span>
          )}
        </div>
        <button
          onClick={runNow}
          disabled={running}
          data-testid="wallet-audit-run"
          className="px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 disabled:bg-slate-300 text-white rounded-lg text-sm font-bold flex items-center gap-2"
        >
          {running ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
          {running ? 'Running…' : 'Run audit now'}
        </button>
      </header>

      <p className="text-xs text-slate-500 mb-3">
        Runs automatically once a week. Compares each user's stored balance against the signed sum of their transactions.
        Any drift &gt; €0.01 or negative <code>reserved</code> is reported here. <strong>Read-only</strong> — never modifies balances.
      </p>

      {loading && (<p className="text-xs text-slate-400"><Loader2 className="w-3 h-3 inline animate-spin mr-1" />Loading…</p>)}
      {ok && (
        <dl className="text-xs grid grid-cols-2 sm:grid-cols-4 gap-x-4 gap-y-1 text-slate-600">
          <div><dt className="font-bold">Last run</dt><dd>{last.ran_at?.slice(0,16).replace('T',' ')}</dd></div>
          <div><dt className="font-bold">Users</dt><dd>{last.total_users}</dd></div>
          <div><dt className="font-bold">Transactions</dt><dd>{last.transactions}</dd></div>
          <div><dt className="font-bold">Discrepancies</dt><dd className={findings.length ? 'text-rose-600 font-bold' : ''}>{findings.length}</dd></div>
        </dl>
      )}
      {findings.length > 0 && (
        <div className="mt-3 max-h-64 overflow-y-auto border border-rose-200 rounded-lg" data-testid="wallet-audit-findings">
          <table className="w-full text-xs">
            <thead className="bg-rose-50 text-rose-700 sticky top-0">
              <tr>
                <th className="px-2 py-1 text-left whitespace-nowrap">Email</th>
                <th className="px-2 py-1 text-right whitespace-nowrap">Stored</th>
                <th className="px-2 py-1 text-right whitespace-nowrap">Expected</th>
                <th className="px-2 py-1 text-right whitespace-nowrap">Diff</th>
                <th className="px-2 py-1 text-left whitespace-nowrap">Note</th>
              </tr>
            </thead>
            <tbody>
              {findings.slice(0,200).map((f) => (
                <tr key={f.user_id} className="border-t border-rose-100">
                  <td className="px-2 py-1 font-mono">{f.email || f.user_id?.slice(0,8)}</td>
                  <td className="px-2 py-1 text-right">€{f.balance?.toFixed(2)}</td>
                  <td className="px-2 py-1 text-right">€{f.expected?.toFixed(2)}</td>
                  <td className={`px-2 py-1 text-right font-bold ${f.diff > 0 ? 'text-emerald-600' : f.diff < 0 ? 'text-rose-600' : 'text-slate-400'}`}>{f.diff > 0 ? '+' : ''}{f.diff?.toFixed(2)}</td>
                  <td className="px-2 py-1 text-xs text-slate-500">{f.note || (f.reserved < 0 ? `reserved=${f.reserved}` : '')}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

